import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BatteryCharging, ArrowLeft, Home as HomeIcon, ClipboardList, Phone, Wrench, CheckCircle2, Loader2, AlertCircle, Send } from "lucide-react";
import { Seo, serviceJsonLd } from "../components/Seo";

const STEPS = [
  { Icon: ClipboardList, t: "1. Fyll i formuläret", d: "Berätta kort om din dator och problemet i formuläret nedan." },
  { Icon: Phone, t: "2. Du får ett förslag", d: "Jag kontaktar dig med ett prisförslag och en tidsplan." },
  { Icon: Wrench, t: "3. Klart – som ny", d: "Vi byter batteriet och du får din dator som ny." },
];

export default function Battery() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", computer_model: "", issue: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.computer_model.trim() || !form.issue.trim()) {
      setError("Fyll i alla fält.");
      return;
    }
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      await api.post("/battery", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Batteribyte på dator"
        description="Håller batteriet inte längre eller varnar datorn för service? Vi hjälper dig med batteribyte på din dator. Fyll i formuläret så återkommer vi med prisförslag och tid."
        path="/batteribyte"
        jsonLd={serviceJsonLd({ name: "Batteribyte på dator", description: "Byte av batteri på bärbara datorer.", path: "/batteribyte" })}
      />
      <Link to="/" data-testid="battery-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <BatteryCharging size={46} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> Snabbt &amp; säkert batteribyte på din dator
      </h1>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Håller batteriet inte längre eller varnar datorn för service?
      </p>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Fyll i formuläret så återkommer jag med ett prisförslag och en tid – enkelt och tryggt.
      </p>

      {done ? (
        <div data-testid="battery-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-10 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för din förfrågan!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Jag har tagit emot dina uppgifter och återkommer så snart som möjligt med prisförslag och tid.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {STEPS.map(({ Icon, t, d }) => (
              <div key={t} className="bg-white border-2 border-gray-300 rounded-xl p-6 flex flex-col items-start">
                <span className="bg-blue-50 text-[#0056D2] rounded-xl p-3">
                  <Icon size={32} strokeWidth={2} />
                </span>
                <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mt-4">{t}</h3>
                <p className="text-base text-[#4A4A4A] mt-2">{d}</p>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Begär prisförslag</h2>
            <p className="text-base text-[#4A4A4A] mt-2">Det tar under en minut. Alla fält behövs för att jag ska kunna ge dig ett bra förslag.</p>

            <div className="space-y-6 mt-6">
              <div>
                <label htmlFor="bat-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
                <input id="bat-name" data-testid="battery-name" value={form.name} onChange={set("name")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
              </div>
              <div>
                <label htmlFor="bat-email" className="text-xl font-bold mb-2 block text-[#1A1A1A]">E-postadress</label>
                <input id="bat-email" type="email" data-testid="battery-email" value={form.email} onChange={set("email")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="namn@exempel.se" />
              </div>
              <div>
                <label htmlFor="bat-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
                <input id="bat-phone" type="tel" data-testid="battery-phone" value={form.phone} onChange={set("phone")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
              </div>
              <div>
                <label htmlFor="bat-model" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Datormodell &amp; tillverkningsår</label>
                <input id="bat-model" data-testid="battery-model" value={form.computer_model} onChange={set("computer_model")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder='t.ex. MacBook Pro 13" 2019' />
              </div>
              <div>
                <label htmlFor="bat-issue" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Beskrivning av problemet</label>
                <textarea id="bat-issue" data-testid="battery-issue" value={form.issue} onChange={set("issue")} rows={5}
                  className="text-lg px-4 py-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] resize-y"
                  placeholder='t.ex. "Batteriet dör snabbt" eller "Varnar för service"' />
              </div>

              {error && (
                <p data-testid="battery-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                  <AlertCircle size={22} /> {error}
                </p>
              )}

              <button data-testid="battery-submit" type="submit" disabled={submitting}
                className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
                {submitting ? <Loader2 className="animate-spin" size={26} /> : <Send size={26} strokeWidth={2.5} />}
                {submitting ? "Skickar…" : "Skicka förfrågan"}
              </button>

              <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
                Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
              </p>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
